import { type CSSProperties, type ReactNode, useLayoutEffect, useRef } from 'react'
import type { CaptionStyle, ClipChart, ClipOverlay, FrameRect } from './clipFactoryApi'

const DISCLAIMER = 'Not financial advice.'
// The highlight caption style's word colour, as the export's (#FFD700).
const HIGHLIGHT = '#FFD700'

interface PreviewOverlayProps {
  overlay: ClipOverlay
  /** Video time now. */
  time: number
  /** Seconds of the clip played so far (cuts left out), for the hook. */
  elapsed: number
  captions: boolean
  /** How the export draws the captions; absent is classic. */
  captionStyle?: CaptionStyle
  watermark: boolean
  hook: string | null
  receiptLines: string[] | null
}

// Sizes are fractions of the exported frame; the layer is a size container, so
// cqh/cqw units scale them to the player as the export scales them to the video.
const box = (r: FrameRect): CSSProperties => ({
  position: 'absolute',
  left: `${r.x * 100}%`,
  top: `${r.y * 100}%`,
  width: `${r.w * 100}%`,
  height: `${r.h * 100}%`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
})
const cqh = (fraction: number) => `${fraction * 100}cqh`
// libass sizes captions by the font's height (ascent + descent, about 1.164 em
// for DejaVu Sans), not by its em as CSS does.
const ASS_EM = 1 / 1.164

/**
 * Text at the largest size up to `max` (a fraction of the frame's height) that
 * fits its box, leaving `shadow` font sizes for the text shadow: what the
 * export's overlay template does (templates/overlay.html fit()).
 */
const Fit = ({ max, shadow = 0.5, style, children }: { max: number; shadow?: number; style?: CSSProperties; children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    const room = el?.parentElement
    const frame = el?.closest('[data-testid="preview-overlay"]') as HTMLElement | null
    if (!el || !room || !frame || !frame.clientHeight) return // not laid out (tests)
    const w = room.clientWidth
    const h = room.clientHeight
    let size = Math.max(1, Math.round(max * frame.clientHeight))
    const apply = () => {
      el.style.fontSize = `${size}px`
      el.style.maxWidth = `${w - size * shadow}px`
    }
    const tooBig = () =>
      el.scrollHeight > h - size * shadow || el.scrollWidth > el.clientWidth + 1 || el.offsetWidth > w - size * shadow + 1
    apply()
    while (size > 4 && tooBig()) {
      size -= 1
      apply()
    }
  })
  return (
    <div ref={ref} style={{ fontSize: cqh(max), ...style }}>
      {children}
    </div>
  )
}

// Burned-in pixels: the same colours in every page theme.
const SHADOW = '0 0 0.12em #000, 0 0 0.12em #000, 0 0.06em 0.2em #000'

/**
 * The captions, hook, watermark, receipt card and "Not financial advice" an
 * export would burn in, drawn over the playing video in the places
 * Clip Factory's export puts them (POST /results/{id}/preview). Text sizes
 * follow the export's; the export also shrinks long text to fit, so this is
 * close, not exact.
 */
/** A chart as the export draws it (overlay.html's "chart" layer): the line, the last close and the window's move. */
const ChartCard = ({ chart }: { chart: ClipChart }) => {
  const c = chart.closes
  const lo = Math.min(...c)
  const span = Math.max(...c) - lo || 1
  const points = c.map((v, i) => `${(i / (c.length - 1)) * 100},${(1 - (v - lo) / span) * 100}`).join(' ')
  const move = ((c[c.length - 1] - c[0]) / c[0]) * 100
  const colour = move >= 0 ? '#22c55e' : '#ef4444'
  const last = c[c.length - 1]
  const price = last.toLocaleString('en-US', { maximumFractionDigits: last >= 100 ? 0 : 4 })
  return (
    <div
      data-testid="preview-chart"
      style={{
        width: '100%',
        height: '100%',
        background: 'rgba(5,10,8,.86)',
        borderRadius: '.45em',
        boxSizing: 'border-box',
        padding: '.5em .7em',
        display: 'flex',
        flexDirection: 'column',
        color: '#f8fafc',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
        <span>
          {chart.coin} ${price}
        </span>
        <span style={{ color: colour }}>
          {move >= 0 ? '+' : ''}
          {move.toFixed(2)}%
        </span>
      </div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ flex: 1, width: '100%', margin: '.3em 0' }}>
        <polyline points={points} fill="none" stroke={colour} strokeWidth={2.2} vectorEffect="non-scaling-stroke" />
      </svg>
      <div style={{ fontSize: '.55em', color: '#cbd5e1', fontWeight: 'normal' }}>{chart.label}</div>
    </div>
  )
}

const PreviewOverlay = ({
  overlay,
  time,
  elapsed,
  captions,
  captionStyle = 'classic',
  watermark,
  hook,
  receiptLines,
}: PreviewOverlayProps) => {
  const caption = captions ? overlay.captions.find((c) => time >= c.start && time < c.end) : null
  // The line at the foot, as the export draws it: the date it was recorded by
  // and/or the disclaimer (a server without strip_text sends only the disclaimer).
  const strip = overlay.strip_text !== undefined ? overlay.strip_text : overlay.disclaimer ? DISCLAIMER : null
  const { frame } = overlay
  // The exported frame inside the player: 9:16 fills it; a wider shape is letterboxed.
  const wide = frame.width / frame.height >= 9 / 16
  const layer: CSSProperties = {
    position: 'absolute',
    aspectRatio: `${frame.width} / ${frame.height}`,
    ...(wide
      ? { left: 0, right: 0, top: '50%', transform: 'translateY(-50%)' }
      : { top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)' }),
    containerType: 'size',
    pointerEvents: 'none',
    fontFamily: '"DejaVu Sans", "Noto Color Emoji", sans-serif',
    fontWeight: 'bold',
    color: '#fff',
  } as CSSProperties

  return (
    <div style={layer} data-testid="preview-overlay" aria-hidden="true">
      {hook && elapsed < overlay.hook_seconds && (
        <div style={box(overlay.hook)} data-testid="preview-hook">
          <Fit max={overlay.hook.h / 2.4} style={{ lineHeight: 1.12, textAlign: 'center', textShadow: SHADOW }}>
            <span style={{ background: 'rgba(0,0,0,.35)', padding: '.04em .22em', borderRadius: '.18em', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' }}>
              {hook}
            </span>
          </Fit>
        </div>
      )}
      {(overlay.charts || [])
        .filter((c) => time >= c.at && time < c.until)
        .slice(0, 1)
        .map((c) => (
          <div key={`${c.coin}-${c.at}`} style={{ ...box(overlay.hook), fontSize: cqh(overlay.hook.h / 7.5) }}>
            <ChartCard chart={c} />
          </div>
        ))}
      {watermark && (
        <div style={{ ...box(overlay.watermark), justifyContent: 'flex-end' }} data-testid="preview-watermark">
          <Fit max={overlay.watermark.h * 0.8} style={{ opacity: 0.82, whiteSpace: 'nowrap', textShadow: '0 0 .15em #000', display: 'flex', alignItems: 'center', gap: '.3em' }} shadow={0.3}>
            <span style={{ display: 'inline-block', width: '.7em', height: '.7em', borderRadius: '.18em', background: '#22c55e' }} />
            <span>
              Fin<span style={{ color: '#22c55e' }}>Creator</span>
            </span>
          </Fit>
        </div>
      )}
      {receiptLines ? (
        <div style={box(overlay.receipt)} data-testid="preview-receipt">
          <Fit
            max={overlay.receipt.h / (strip ? 3.6 : 3)}
            shadow={0}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'rgba(5,10,8,.86)',
              border: '.08em solid #22c55e88',
              borderRadius: '.45em',
              padding: '.35em .6em',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              lineHeight: 1.25,
              fontWeight: 'normal',
              color: '#f8fafc',
            }}
          >
            {receiptLines.map((line, i) => (
              <div key={i} style={{ color: i === receiptLines.length - 1 ? '#bbf7d0' : undefined }}>
                {line}
              </div>
            ))}
            {strip && <div style={{ color: '#cbd5e1', fontSize: '.78em' }}>{strip}</div>}
          </Fit>
        </div>
      ) : (
        strip && (
          <div style={box(overlay.disclaimer_strip)} data-testid="preview-disclaimer">
            <Fit max={overlay.disclaimer_strip.h * 0.7} style={{ fontWeight: 'normal', opacity: 0.9, whiteSpace: 'nowrap', textShadow: '0 0 .15em #000, 0 0 .15em #000' }}>
              {strip}
            </Fit>
          </div>
        )
      )}
      {caption && (
        <div
          data-testid="preview-caption"
          style={{
            position: 'absolute',
            left: `${overlay.caption.side * 100}%`,
            right: `${overlay.caption.side * 100}%`,
            bottom: `${overlay.caption.bottom * 100}%`,
            textAlign: 'center',
            fontSize: cqh(overlay.caption.font * ASS_EM),
            lineHeight: 1.2,
            ...(captionStyle === 'boxed'
              ? { background: 'rgba(0, 0, 0, 0.75)', padding: '0.1em 0.25em', boxDecorationBreak: 'clone' as const }
              : { WebkitTextStroke: `${cqh(overlay.caption.outline * 2 * ASS_EM)} #000`, paintOrder: 'stroke fill' }),
          }}
          data-style={captionStyle}
        >
          {captionStyle === 'highlight' && caption.words?.length
            ? caption.words.map((w, i) => {
                // As the export: a word is lit from its start until the next word's.
                const next = caption.words?.[i + 1]
                const lit = time >= (i === 0 ? caption.start : w.start) && (!next || time < next.start)
                return (
                  <span key={i} style={lit ? { color: HIGHLIGHT } : undefined} data-lit={lit || undefined}>
                    {i > 0 ? ' ' : ''}
                    {w.text}
                  </span>
                )
              })
            : caption.text}
        </div>
      )}
    </div>
  )
}

export default PreviewOverlay
